/**
 * Writes every supplier pack into a folder on the office machine using the
 * browser's File System Access API (Chrome and Edge). The folder is then
 * zipped by hand and sent on, so no zip library is needed.
 *
 * Layout (see docs/supplier-audit-export.md):
 *   supplier_audit_<start>/_index.xlsx
 *   supplier_audit_<start>/<resorts|golf_courses|transfers>/supplier_audit_<name>.xlsx
 */

import { sheetsToExcelBytes } from "../export"
import { buildSupplierWorkbookSheets } from "./rows"
import { buildIndexSheets } from "./packs"

/**
 * Whether this browser can write straight into a folder.
 */
export const canWriteFolders = () => typeof window !== "undefined" && typeof window.showDirectoryPicker === "function"

/**
 * Raised when the export folder already exists in the chosen location, so an
 * earlier run is never silently mixed with this one.
 */
export class FolderExistsError extends Error {
	constructor(name) {
		super(`A folder called "${name}" already exists there. Pick another location or delete the old folder first.`)
		this.name = "FolderExistsError"
	}
}

const writeFile = async (directory, fileName, bytes) => {
	const handle = await directory.getFileHandle(fileName, { create: true })
	const writable = await handle.createWritable()
	await writable.write(bytes)
	await writable.close()
}

const folderExists = (parent, name) =>
	parent
		.getDirectoryHandle(name)
		.then(() => true)
		.catch((error) => (error.name === "NotFoundError" ? false : Promise.reject(error)))

// Let the progress line repaint between files
const nextFrame = () => new Promise((resolve) => setTimeout(resolve, 0))

/**
 * Ask where to save. Must be the first await in a click handler, because the
 * browser only opens the picker in direct response to the click.
 *
 * @returns {Promise<FileSystemDirectoryHandle>} Rejects with an AbortError when cancelled
 */
export const pickParentFolder = () => window.showDirectoryPicker({ id: "supplier-audit", mode: "readwrite" })

/**
 * Create `folderName` inside `parent` and write the index plus one workbook per
 * pack into it.
 *
 * @param {Object} options
 * @param {FileSystemDirectoryHandle} options.parent
 * @param {string} options.folderName
 * @param {import("./types").AuditResult} options.result
 * @param {{ packs: import("./packs").SupplierPack[], checks: import("./packs").PackCheck[] }} options.plan
 * @param {({ done: number, total: number }) => void} [options.onProgress]
 */
export async function writeSupplierPacks({ parent, folderName, result, plan, onProgress = () => {} }) {
	if (await folderExists(parent, folderName)) throw new FolderExistsError(folderName)

	const root = await parent.getDirectoryHandle(folderName, { create: true })
	const total = plan.packs.length + 1
	onProgress({ done: 0, total })

	await writeFile(root, "_index.xlsx", sheetsToExcelBytes(buildIndexSheets(result, plan)))
	onProgress({ done: 1, total })

	const folders = new Map()
	for (let index = 0; index < plan.packs.length; index++) {
		const pack = plan.packs[index]
		if (!folders.has(pack.folder)) folders.set(pack.folder, await root.getDirectoryHandle(pack.folder, { create: true }))

		await writeFile(folders.get(pack.folder), pack.fileName, sheetsToExcelBytes(buildSupplierWorkbookSheets(result, pack.keys)))
		onProgress({ done: index + 2, total })
		await nextFrame()
	}
}
